import { useCallback, useEffect, useState } from "react";

import Card from "../../../components/ui/Card";
import Button from "../../../components/ui/Button";
import {
  dispatchOrder,
  fetchOrder,
  openLabel,
  payOrder,
  simulatePayment,
  SimulatedOutcome,
} from "../api";
import { OrderDetail } from "../types";

type OrderPanelProps = {
  orderId: string;
  onClose: () => void;
  onChanged?: () => void;
};

const STATUS_LABEL: Record<string, string> = {
  cotizada: "Cotizada",
  pago_pendiente: "Pago pendiente",
  pagada: "Pagada",
  pago_rechazado: "Pago rechazado",
  enviando: "Enviando",
  enviada: "Enviada",
  envio_fallido: "Envío fallido",
  cancelada: "Cancelada",
};

const STATUS_COLOR: Record<string, string> = {
  cotizada: "var(--text-secondary)",
  pago_pendiente: "var(--warning, #b7791f)",
  pagada: "var(--success, #2f855a)",
  pago_rechazado: "var(--danger)",
  enviando: "var(--warning, #b7791f)",
  enviada: "var(--success, #2f855a)",
  envio_fallido: "var(--danger)",
  cancelada: "var(--text-secondary)",
};

function formatPrice(amount: number, currency: string) {
  return `$ ${Number(amount).toLocaleString("es-AR")} ${currency}`;
}

function formatFecha(iso: string) {
  const fecha = new Date(iso);

  return Number.isNaN(fecha.getTime()) ? iso : fecha.toLocaleString("es-AR");
}

// El link de pago abierto más reciente, para volver a mostrarlo aunque se
// haya recargado la página.
function ultimoLink(orden: OrderDetail): string | null {
  const conLink = [...orden.payments].reverse().find((pago) => pago.initPoint);

  return conLink?.initPoint ?? null;
}

// La vista de una orden y todo lo que se puede hacer con ella según su
// estado. Las acciones no repiten la máquina de estados: el servidor decide;
// acá solo se ofrecen los botones que tienen sentido y se muestra lo que él
// conteste.
export function OrderPanel({ orderId, onClose, onChanged }: OrderPanelProps) {
  const [orden, setOrden] = useState<OrderDetail | null>(null);
  const [linkDePago, setLinkDePago] = useState<string | null>(null);
  const [simulado, setSimulado] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");

  const recargar = useCallback(async () => {
    try {
      const data = await fetchOrder(orderId);
      setOrden(data);
      setLinkDePago(ultimoLink(data));
      setSimulado(Boolean(ultimoLink(data)?.startsWith("simulado://")));
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo leer la orden.");
    }
  }, [orderId]);

  useEffect(() => {
    recargar();
  }, [recargar]);

  const accion = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError("");
    setAviso("");

    try {
      await fn();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "La acción falló.");
    } finally {
      setBusy(false);
    }
  };

  const abrirCobro = () =>
    accion(async () => {
      const pago = await payOrder(orderId);
      setOrden(pago.order);
      setLinkDePago(pago.initPoint);
      setSimulado(pago.simulado);
      setAviso("Cobro abierto: compartile el link al cliente.");
    });

  const simular = (resultado: SimulatedOutcome) =>
    accion(async () => {
      const data = await simulatePayment(orderId, resultado);
      setOrden(data);
      setLinkDePago(ultimoLink(data));
    });

  const despachar = () =>
    accion(async () => {
      const resultado = await dispatchOrder(orderId);
      setOrden(resultado.order);

      if (!resultado.ok && resultado.error) {
        setError(`El despacho falló: ${resultado.error}`);
      } else {
        setAviso(`Despachada. Tracking: ${resultado.order.trackingNumber ?? "-"}`);
      }
    });

  const copiarLink = () =>
    accion(async () => {
      if (!linkDePago) return;
      await navigator.clipboard.writeText(linkDePago);
      setAviso("Link copiado al portapapeles.");
    });

  const compartirWhatsApp = () => {
    if (!linkDePago || !orden) return;

    const texto = `Hola ${orden.recipient?.name ?? ""}! Para pagar tu envío (${formatPrice(
      orden.price.amount,
      orden.price.currency
    )}) entrá acá: ${linkDePago}`;

    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, "_blank", "noopener");
  };

  if (!orden) {
    return (
      <Card>
        {error ? (
          <div style={{ color: "var(--danger)", fontWeight: 600 }}>{error}</div>
        ) : (
          <div style={{ color: "var(--text-secondary)" }}>Cargando la orden…</div>
        )}
      </Card>
    );
  }

  const puedePagar = ["cotizada", "pago_pendiente", "pago_rechazado"].includes(orden.status);
  const puedeDespachar = ["pagada", "envio_fallido"].includes(orden.status);
  const mostrarLink = Boolean(linkDePago) && orden.status === "pago_pendiente";

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, color: "var(--text-primary)", fontSize: 20 }}>
            Orden #{orden.id}
          </h2>
          <p style={{ margin: "6px 0 0", color: "var(--text-secondary)", fontSize: 13 }}>
            {orden.productType} · {orden.deliveredType === "D" ? "domicilio" : "sucursal"} ·{" "}
            {orden.postalCodeOrigin} → {orden.postalCodeDestination} · creada{" "}
            {formatFecha(orden.createdAt)}
          </p>
        </div>

        <Button variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", margin: "16px 0 4px", flexWrap: "wrap" }}>
        <span
          style={{
            padding: "4px 12px",
            borderRadius: 999,
            border: `1px solid ${STATUS_COLOR[orden.status] ?? "var(--border-color)"}`,
            color: STATUS_COLOR[orden.status] ?? "var(--text-primary)",
            fontSize: 13,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          {STATUS_LABEL[orden.status] ?? orden.status}
        </span>

        <span style={{ color: "var(--text-primary)", fontWeight: 800, fontSize: 18 }}>
          {formatPrice(orden.price.amount, orden.price.currency)}
        </span>

        {orden.trackingNumber && (
          <span style={{ color: "var(--text-secondary)", fontSize: 13 }}>
            Tracking: <strong style={{ color: "var(--text-primary)" }}>{orden.trackingNumber}</strong>
          </span>
        )}
      </div>

      {orden.statusReason && (
        <p style={{ margin: "4px 0 0", color: "var(--text-secondary)", fontSize: 13 }}>
          {orden.statusReason}
        </p>
      )}

      <p style={{ margin: "10px 0 0", color: "var(--text-secondary)", fontSize: 13 }}>
        Destinatario: <strong style={{ color: "var(--text-primary)" }}>{orden.recipient?.name}</strong>
        {" · "}{orden.recipient?.phone} · {orden.recipient?.email}
        {orden.agencyId ? ` · sucursal ${orden.agencyId}` : ""}
      </p>

      {(error || aviso) && (
        <div
          style={{
            marginTop: 12,
            color: error ? "var(--danger)" : "var(--success, #2f855a)",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {error || aviso}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 16 }}>
        {puedePagar && (
          <Button variant="primary" onClick={abrirCobro} disabled={busy}>
            {orden.status === "pago_rechazado"
              ? "Reintentar pago"
              : orden.status === "pago_pendiente"
                ? "Ver link de pago"
                : "Abrir cobro"}
          </Button>
        )}

        {mostrarLink && (
          <>
            <Button variant="secondary" onClick={copiarLink} disabled={busy}>
              Copiar link
            </Button>
            <Button variant="secondary" onClick={compartirWhatsApp} disabled={busy}>
              Compartir por WhatsApp
            </Button>
          </>
        )}

        {puedeDespachar && (
          <Button variant="primary" onClick={despachar} disabled={busy}>
            {orden.status === "envio_fallido" ? "Reintentar despacho" : "Despachar"}
          </Button>
        )}

        {orden.status === "enviada" && (
          <Button
            variant="secondary"
            onClick={() => accion(() => openLabel(orderId))}
            disabled={busy}
          >
            Descargar etiqueta
          </Button>
        )}

        <Button variant="secondary" onClick={() => accion(recargar)} disabled={busy}>
          Actualizar
        </Button>
      </div>

      {mostrarLink && simulado && (
        <div
          style={{
            marginTop: 14,
            border: "1px dashed var(--border-color)",
            borderRadius: 14,
            padding: 14,
            display: "grid",
            gap: 10,
          }}
        >
          <span style={{ color: "var(--text-secondary)", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
            Modo simulado — decidir el desenlace del pago
          </span>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Button variant="secondary" onClick={() => simular("aprobado")} disabled={busy}>
              Aprobar
            </Button>
            <Button variant="secondary" onClick={() => simular("rechazado")} disabled={busy}>
              Rechazar
            </Button>
            <Button variant="secondary" onClick={() => simular("pendiente")} disabled={busy}>
              Dejar pendiente
            </Button>
          </div>
        </div>
      )}

      {mostrarLink && !simulado && linkDePago && (
        <p style={{ margin: "12px 0 0", color: "var(--text-secondary)", fontSize: 13, wordBreak: "break-all" }}>
          Link de pago: <a href={linkDePago} target="_blank" rel="noreferrer">{linkDePago}</a>
        </p>
      )}

      {orden.payments.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <h3 style={{ margin: "0 0 8px", color: "var(--text-primary)", fontSize: 15 }}>Pagos</h3>
          <div style={{ display: "grid", gap: 6 }}>
            {orden.payments.map((pago) => (
              <div key={pago.id} style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                {formatFecha(pago.createdAt)} · <strong style={{ color: "var(--text-primary)" }}>{pago.status}</strong>
                {pago.statusDetail ? ` (${pago.statusDetail})` : ""} · {formatPrice(pago.amount, pago.currency)}
                {pago.mpPaymentId ? ` · ${pago.mpPaymentId}` : ""}
              </div>
            ))}
          </div>
        </div>
      )}

      {orden.events.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <h3 style={{ margin: "0 0 8px", color: "var(--text-primary)", fontSize: 15 }}>Historial</h3>
          <div style={{ display: "grid", gap: 6 }}>
            {orden.events.map((evento) => (
              <div key={evento.id} style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                {formatFecha(evento.createdAt)} ·{" "}
                <strong style={{ color: "var(--text-primary)" }}>
                  {evento.from ? `${evento.from} → ` : ""}{evento.to}
                </strong>
                {evento.reason ? ` · ${evento.reason}` : ""} · {evento.actor}
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
